'use client';

import * as Clerk from '@clerk/elements/common';
import * as SignIn from '@clerk/elements/sign-in';
import Link from 'next/link';

function FitventWordmark() {
  return (
    <div style={{ marginBottom: '32px', textAlign: 'center' }}>
      <span
        style={{
          fontFamily: 'var(--font-barlow-condensed)',
          fontWeight: 700,
          fontSize: '28px',
          letterSpacing: '0.22em',
          color: '#FFFFFF',
          textTransform: 'uppercase',
          display: 'block',
        }}
      >
        FITVENT
      </span>
      <div
        style={{
          width: '28px',
          height: '2px',
          background: '#006D6D',
          margin: '6px auto 0',
        }}
      />
    </div>
  );
}

function FieldWrapper({
  name,
  label,
  children,
}: {
  name: string;
  label: string;
  children?: React.ReactNode;
}) {
  return (
    <Clerk.Field name={name} className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <Clerk.Label
          className="text-sm font-medium"
          style={{ fontFamily: 'var(--font-instrument-sans)', color: '#9C9088' }}
        >
          {label}
        </Clerk.Label>
        {children}
      </div>
      <Clerk.Input
        className="h-11 w-full rounded-md border px-3 text-sm outline-none transition-colors"
        style={{
          fontFamily: 'var(--font-instrument-sans)',
          background: '#1C1B18',
          color: '#FFFFFF',
          borderColor: 'rgba(255,255,255,0.10)',
        }}
      />
      <Clerk.FieldError
        className="text-xs"
        style={{ color: '#EF4444' }}
      />
    </Clerk.Field>
  );
}

function SubmitButton({ children }: { children: React.ReactNode }) {
  return (
    <SignIn.Action
      submit
      className="h-11 w-full rounded-md text-sm font-semibold uppercase tracking-widest text-white transition-colors disabled:opacity-50"
      style={{
        fontFamily: 'var(--font-barlow-condensed)',
        background: '#006D6D',
      }}
    >
      <Clerk.Loading>
        {(isLoading) =>
          isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="h-4 w-4 animate-spin"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeDasharray="60"
                  strokeDashoffset="15"
                  strokeLinecap="round"
                />
              </svg>
              Signing in…
            </span>
          ) : (
            children
          )
        }
      </Clerk.Loading>
    </SignIn.Action>
  );
}

export default function SignInPage() {
  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center p-6"
      style={{
        background: '#0F0E0C',
        backgroundImage:
          'repeating-linear-gradient(15deg, transparent 0 40px, rgba(255,255,255,0.018) 40px 41px)',
      }}
    >
      <FitventWordmark />

      <SignIn.Root>
        <div
          className="w-full max-w-sm rounded-lg border p-8"
          style={{
            background: '#1A1917',
            borderColor: 'rgba(255,255,255,0.08)',
          }}
        >
          {/* ── Step 1: Email + Password ── */}
          <SignIn.Step name="start" className="flex flex-col gap-5">
            <h1
              className="text-center text-lg font-bold uppercase tracking-widest"
              style={{
                fontFamily: 'var(--font-barlow-condensed)',
                color: '#FFFFFF',
              }}
            >
              Sign In
            </h1>

            <Clerk.GlobalError
              className="rounded-md px-3 py-2 text-xs"
              style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}
            />

            <div className="flex flex-col gap-3">
              <Clerk.Connection
                name="google"
                className="flex h-11 w-full items-center justify-center gap-3 rounded-md border text-sm font-medium text-white transition-colors"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  background: '#1C1B18',
                  borderColor: 'rgba(255,255,255,0.10)',
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Continue with Google
              </Clerk.Connection>

              <Clerk.Connection
                name="apple"
                className="flex h-11 w-full items-center justify-center gap-3 rounded-md border text-sm font-medium text-white transition-colors"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  background: '#1C1B18',
                  borderColor: 'rgba(255,255,255,0.10)',
                }}
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="white">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
                Continue with Apple
              </Clerk.Connection>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.08)' }} />
              <span
                className="text-xs uppercase tracking-wider"
                style={{ fontFamily: 'var(--font-barlow-condensed)', color: '#5C5650' }}
              >
                or
              </span>
              <div className="h-px flex-1" style={{ background: 'rgba(255,255,255,0.08)' }} />
            </div>

            <FieldWrapper name="identifier" label="Email address" />
            <FieldWrapper name="password" label="Password" />

            <div className="flex items-center justify-between">
              <Link
                href="/forgot-password"
                className="text-xs transition-colors hover:underline"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  color: '#006D6D',
                }}
              >
                Forgot password?
              </Link>
            </div>

            <SubmitButton>Sign In</SubmitButton>

            <p
              className="text-center text-sm"
              style={{
                fontFamily: 'var(--font-instrument-sans)',
                color: '#9C9088',
              }}
            >
              Don&apos;t have an account?{' '}
              <Link
                href="/sign-up"
                className="transition-colors hover:underline"
                style={{ color: '#006D6D' }}
              >
                Sign up
              </Link>
            </p>
          </SignIn.Step>

          {/* ── Step 2: Verification (2FA / email code) ── */}
          <SignIn.Step name="verifications" className="flex flex-col gap-5">
            <h1
              className="text-center text-lg font-bold uppercase tracking-widest"
              style={{
                fontFamily: 'var(--font-barlow-condensed)',
                color: '#FFFFFF',
              }}
            >
              Verify
            </h1>

            <Clerk.GlobalError
              className="rounded-md px-3 py-2 text-xs"
              style={{ background: 'rgba(239,68,68,0.10)', color: '#EF4444' }}
            />

            <SignIn.Strategy name="email_code">
              <p
                className="text-center text-sm"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  color: '#9C9088',
                }}
              >
                We sent a verification code to your email.
              </p>
              <FieldWrapper name="code" label="Verification code" />
              <SubmitButton>Verify</SubmitButton>
              <SignIn.Action
                resend
                className="text-center text-xs transition-colors hover:underline disabled:opacity-50"
                style={{ fontFamily: 'var(--font-instrument-sans)', color: '#9C9088' }}
                fallback={({ resendableAfter }) => (
                  <p
                    className="text-center text-xs"
                    style={{ fontFamily: 'var(--font-instrument-sans)', color: '#6B6661' }}
                  >
                    Resend code in {resendableAfter}s
                  </p>
                )}
              >
                Didn&rsquo;t get a code? Resend
              </SignIn.Action>
            </SignIn.Strategy>

            <SignIn.Strategy name="phone_code">
              <p
                className="text-center text-sm"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  color: '#9C9088',
                }}
              >
                We sent a verification code to your phone.
              </p>
              <FieldWrapper name="code" label="Verification code" />
              <SubmitButton>Verify</SubmitButton>
              <SignIn.Action
                resend
                className="text-center text-xs transition-colors hover:underline disabled:opacity-50"
                style={{ fontFamily: 'var(--font-instrument-sans)', color: '#9C9088' }}
                fallback={({ resendableAfter }) => (
                  <p
                    className="text-center text-xs"
                    style={{ fontFamily: 'var(--font-instrument-sans)', color: '#6B6661' }}
                  >
                    Resend code in {resendableAfter}s
                  </p>
                )}
              >
                Didn&rsquo;t get a code? Resend
              </SignIn.Action>
            </SignIn.Strategy>

            <SignIn.Strategy name="totp">
              <p
                className="text-center text-sm"
                style={{
                  fontFamily: 'var(--font-instrument-sans)',
                  color: '#9C9088',
                }}
              >
                Enter the code from your authenticator app.
              </p>
              <FieldWrapper name="code" label="Authenticator code" />
              <SubmitButton>Verify</SubmitButton>
            </SignIn.Strategy>

            <SignIn.Action
              navigate="start"
              className="text-center text-xs transition-colors hover:underline"
              style={{ color: '#006D6D' }}
            >
              Go back
            </SignIn.Action>
          </SignIn.Step>

        </div>
      </SignIn.Root>
    </div>
  );
}
